<template>
  <div v-if="!!phaseData">
    <field-label label="Critérios Clínicos" class="mb-6">
      <v-expansion-panels accordion class="mt-3">
        <v-expansion-panel>
          <v-expansion-panel-header> SCORAD </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="d-flex justify-space-between">
              <field-label
                titleClass="text-h6"
                titleColorClass="grey--text text--darken-2"
                label="Marque, por favor, as áreas afetadas"
                class="mx-4"
              />
              <info-btn :size="'medium'" @click="setImageToShow('/assets/scoradinfo.png')" />
            </div>
            <div class="d-flex mt-6" :class="{ 'flex-column': $vuetify.breakpoint.smAndDown }">
              <atopic-dermatitis-body-model
                v-model="phaseData.scorad['areas_afetadas']"
                height="600px"
                class="ma-6 d-flex justify-center"
                style="flex-basis: 50%"
                width="100%"
                v-if="isPatientAdult"
                :style="{
                  order: $vuetify.breakpoint.smAndDown ? 4 : 'auto',
                }"
              />
              <atopic-dermatitis-child-body
                v-model="phaseData.scorad['areas_afetadas']"
                height="600px"
                class="ma-6 d-flex justify-center"
                style="flex-basis: 50%"
                width="100%"
                v-else
                :style="{
                  order: $vuetify.breakpoint.smAndDown ? 4 : 'auto',
                }"
              />
              <v-divider
                :vertical="$vuetify.breakpoint.mdAndUp"
                :class="{
                  'ml-auto': $vuetify.breakpoint.mdAndUp,
                  'my-4': $vuetify.breakpoint.smAndDown,
                }"
                :style="{
                  order: $vuetify.breakpoint.smAndDown ? 2 : 'auto',
                }"
              />
              <div
                class="d-flex flex-column"
                :class="{ 'pl-12': $vuetify.breakpoint.mdAndUp }"
                :style="{
                  'flex-basis': $vuetify.breakpoint.smAndDown ? 'auto' : '50%',
                  order: $vuetify.breakpoint.smAndDown ? 1 : 'auto',
                }"
              >
                <span class="font-weight-bold text-h6 mb-2">Áreas afetadas</span>

                <span v-if="affectedAreas.length === 0">Sem lesões</span>
                <div class="d-flex flex-column my-2">
                  <span v-for="area in affectedAreas" :key="area.key" class="d-flex flex-column mt-1">
                    {{ area.label }}</span
                  >
                </div>
              </div>
            </div>
            <div class="mt-6">
              <v-row class="justify-space-between">
                <v-col cols="5">
                  <v-row v-for="option in symptomOptions().col1" :key="option.key">
                    <v-col cols="12" class="pb-0">
                      <field-label :label="option.label" />
                    </v-col>
                    <v-col v-for="level in option.images_by_level" :key="option.key + '_' + level.level" class="pt-0">
                      <v-radio-group v-model="phaseData.scorad.niveis_sintoma[option.key].value">
                        <v-img
                          :src="level.image"
                          :lazy-src="lazySource"
                          width="80px"
                          aspect-ratio="1"
                          class="br-20 zoomable"
                          @click="setImageToShow(level.image)"
                        />
                        <div class="image-radio">
                          <v-radio :value="level.level" />
                        </div>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-col>
                <v-col cols="5">
                  <v-row v-for="option in symptomOptions().col2" :key="option.key">
                    <v-col cols="12" class="pb-0">
                      <field-label :label="option.label" />
                    </v-col>
                    <v-col v-for="level in option.images_by_level" :key="option.key + '_' + level.level" class="pt-0">
                      <v-radio-group v-model="phaseData.scorad.niveis_sintoma[option.key].value">
                        <v-img
                          :src="level.image"
                          :lazy-src="lazySource"
                          width="80px"
                          aspect-ratio="1"
                          class="br-20 zoomable"
                          @click="setImageToShow(level.image)"
                        />
                        <div class="image-radio">
                          <v-radio :value="level.level" />
                        </div>
                      </v-radio-group>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </div>
            <div class="rate-ten">
              <div>
                <rate-ten v-model="phaseData.scorad.coceira" :title="'Coceira'" />
                <rate-ten v-model="phaseData.scorad.sono" :title="'Impacto no Sono'" />
              </div>
            </div>
            <field-label class="mt-8" label="Anotações">
              <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.scorad.notes" />
            </field-label>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header> EASI+BSA </v-expansion-panel-header>
          <v-expansion-panel-content>
            <field-label label="Resultado dos testes" titleClass="text-h6" titleColorClass="grey--text text--darken-2">
              <div
                class="mt-4 d-flex flex-row"
                :class="{
                  'flex-column gap-8': $vuetify.breakpoint.smAndDown,
                  'gap-12': !$vuetify.breakpoint.smAndDown,
                }"
              >
                <field-label v-for="indicator in indicators" :key="indicator.key" :label="indicator.name">
                  <template v-slot:label-extra v-if="indicator.key === 'easi'">
                    <info-btn @click="setImageToShow('/assets/easi.png')" />
                  </template>
                  <div class="d-flex gap-2 mt-2 align-center">
                    <text-field
                      :min="0"
                      type="number"
                      v-if="phaseData.easi_bsa.resultado_manual"
                      v-model="phaseData.easi_bsa[indicator.key]"
                      :max="indicator.max"
                    />
                    <span
                      v-else
                      :class="{
                        'red--text': indicator.getComputedValue() > 9,
                        'black--text text-h6': indicator.getComputedValue() == null,
                        'big-number': indicator.getComputedValue() != null,
                      }"
                      >{{ indicator.getComputedValue() == null ? 'Indefinido' : indicator.getComputedValue() }}</span
                    >
                    <span
                      v-if="indicator.getComputedValue()"
                      class="ml-2"
                      :class="{ 'red--text': indicator.getComputedValue() > 9 }"
                      style="width: 120px"
                      >{{ indicator.getComputedValue() > 9 ? 'Moderado a grave' : 'Leve' }}</span
                    >
                  </div>
                </field-label>
              </div>

              <v-switch
                class="my-4"
                label="Aplicar resultado manualmente"
                v-model="phaseData.easi_bsa.resultado_manual"
                hide-details
              />
              <area-slider
                :data="phaseData.easi_bsa"
                :area="areas"
                :types="skinProblemTypes"
                :max="3"
                v-if="!phaseData.easi_bsa.resultado_manual"
              />
              <field-label class="mt-8" label="Anotações">
                <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.easi_bsa.notes" />
              </field-label>
            </field-label>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header
            v-if="
              !(
                attendance.data.summarizedFlow &&
                pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW == ECustomSummmarizedflow.SANOFI
              )
            "
          >
            IGA
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-radio-group v-model="phaseData.iga.value">
              <v-row
                :class="$vuetify.breakpoint.smAndDown ? 'table' : 'table mr-6'"
                v-for="option in igaOptions"
                :key="option.value"
              >
                <v-col cols="1">
                  <v-radio :value="option.value" />
                </v-col>
                <v-col cols="2">
                  <div class="d-flex align-center justify-start">{{ option.value }}</div>
                </v-col>
                <v-col :cols="$vuetify.breakpoint.smAndDown ? 8 : 3">
                  <div class="d-flex align-center justify-start">{{ option.intensity }}</div>
                </v-col>
                <v-col :cols="$vuetify.breakpoint.smAndDown ? 12 : 6">
                  <div class="d-flex align-center justify-start">{{ option.description }}</div>
                </v-col>
              </v-row>
            </v-radio-group>
            <v-row class="justify-center mt-6">
              <v-col cols="11">
                <ol>
                  <li>
                    Pacientes com lesões intensamente vermelhas, elevadas e liquenificadas mas limitadas em extensão,
                    devem ser considerados Moderados - 3.
                  </li>
                  <li>Escoriações não são avaliadas nessa escala</li>
                </ol>
              </v-col>
            </v-row>
            <v-row class="justify-center">
              <v-col cols="11">
                <p>
                  Referência:
                  <a
                    href="https://www.eczemacouncil.org/assets/docs/Validated-Investigator-Global-Assessment-Scale_vIGA-AD_2017.pdf"
                    target="_blank"
                  >
                    https://www.eczemacouncil.org/assets/docs/Validated-Investigator-Global-Assessment-Scale_vIGA-AD_2017.pdf
                  </a>
                </p>
              </v-col>
            </v-row>
            <field-label class="mt-8" label="Anotações">
              <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.iga.notes" />
            </field-label>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel v-if="pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW === ECustomSummmarizedflow.SANOFI">
          <v-expansion-panel-header>DLQI</v-expansion-panel-header>
          <v-expansion-panel-content>
            <div class="mb-2 cuq2ol-header">ÍNDICE DE QUALIDADE DE VIDA EM DERMATOLOGIA</div>
            <v-switch class="my-4" label="Aplicar resultado manualmente" v-model="phaseData.dlqi.manual" hide-details />
            <v-row>
              <div class="col-sm-3 col-md-12 col-lg-3 col">
                <v-menu
                  ref="pickDlqiDate"
                  v-model="pickDlqiDate"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  max-width="290px"
                  min-width="auto"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field
                      :value="phaseData.dlqi.date ? $moment(phaseData.dlqi.date).format('DD/MM/YYYY') : 'Data'"
                      solo
                      v-bind="attrs"
                      v-on="on"
                    />
                  </template>
                  <v-date-picker
                    v-model="phaseData.dlqi.date"
                    no-title
                    @change="pickDlqiDate = false"
                    locale="pt-br"
                    :max="$moment().subtract(1, 'days').toISOString()"
                  ></v-date-picker>
                </v-menu>
              </div>
            </v-row>
            <div class="mt-4 d-flex flex-row gap-12">
              <field-label label="DLQI">
                <div class="d-flex gap-2 mt-2 align-center">
                  <text-field
                    :min="0"
                    type="number"
                    v-if="phaseData.dlqi.manual"
                    v-model="phaseData.dlqi.value"
                    :max="30"
                  />
                  <span
                    v-else
                    :class="{
                      'red--text': phaseData.dlqi.value > 9,
                      'black--text text-h6': phaseData.dlqi.value == null,
                      'big-number': phaseData.dlqi.value != null,
                    }"
                    >{{ phaseData.dlqi.value == null ? 'Indefinido' : phaseData.dlqi.value }}</span
                  >
                  <span
                    v-if="phaseData.dlqi.value"
                    class="ml-2"
                    :class="{ 'red--text': phaseData.dlqi.value > 9 }"
                    style="width: 120px"
                    >{{ phaseData.dlqi.value > 9 ? 'Moderado a grave' : 'Leve' }}</span
                  >
                </div>
              </field-label>
            </div>

            <div class="mt-12" v-if="!phaseData.dlqi.manual">
              <radio-options
                class="mb-2"
                v-for="dlqiOption in dlqiOptions"
                :key="dlqiOption.key"
                v-show="
                  dlqiOption.key !== 'impedimento_trabalho_estudo_passado' ||
                  phaseData.dlqi.profile['impedimento_trabalho_estudo_atual'].value === 0
                "
                :label="dlqiOption.name"
                v-model="phaseData.dlqi.profile[dlqiOption.key].value"
                row
                clearable
                :options="dlqiOption.options || defaultDlqiOptions"
                @input="checkDlqi(dlqiOption.key, $event)"
              />
            </div>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </field-label>
    <field-label :label="'Critérios de Sintomas'">
      <v-expansion-panels accordion class="mt-3">
        <v-expansion-panel v-if="pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW !== ECustomSummmarizedflow.SANOFI">
          <v-expansion-panel-header> WI-NRS </v-expansion-panel-header>
          <v-expansion-panel-content>
            <field-label
              :label="'Numa escala de 0 a 10, dê uma nota para o pior momento de coceira, nas últimas 24 horas:'"
            >
              <gradient-range :color="'#1976d2'" :cols="11" v-model="phaseData.winrs.value" />

              <v-row class="justify-center mt-6">
                <v-col cols="11">
                  <p>
                    Referência: Yosipovitch, G., et al. "Peak Pruritus Numerical Rating Scale: psychometric validation
                    and responder definition for assessing itch in moderate‐to‐severe atopic dermatitis." British
                    Journal of Dermatology 181.4 (2019): 761-769.
                  </p>
                </v-col>
              </v-row>
              <field-label class="mt-8" label="Anotações">
                <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.winrs.notes" />
              </field-label>
            </field-label>
          </v-expansion-panel-content>
        </v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header
            v-if="
              !attendance.data.summarizedFlow ||
              pharmaceuticalDomainConfig.CUSTOMSUMMARIZEDFLOW == ECustomSummmarizedflow.SANOFI
            "
          >
            POEM
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <field-label :label="'Pontuação total da POEM (Máximo de 28)'">
              <div class="d-flex gap-2 mt-2 align-center">
                <text-field
                  :min="0"
                  type="number"
                  v-if="phaseData.poem.resultado_manual"
                  v-model="phaseData.poem.value"
                  :max="28"
                />
                <span
                  v-else
                  :class="phaseData.poem.value === '?' ? 'black--text text-h6' : 'big-number ' + poemSeverity.class"
                  >{{ !phaseData.poem.value ? '?' : phaseData.poem.value }}</span
                >
                <span v-if="phaseData.poem.value" class="ml-2" :class="poemSeverity.class" style="width: 120px">{{
                  poemSeverity.text
                }}</span>
              </div>
              <v-switch
                class="my-4"
                label="Aplicar resultado manualmente"
                v-model="phaseData.poem.resultado_manual"
                hide-details
              />
            </field-label>
            <template v-if="!phaseData.poem.resultado_manual">
              <field-label v-for="question in poemQuestions.questions" :key="question.key" :label="question.name">
                <v-radio-group
                  v-model="phaseData.poem.answers[question.key].value"
                  :row="$vuetify.breakpoint.smAndDown ? false : true"
                >
                  <v-radio
                    v-for="option in poemQuestions.options"
                    :key="question.key + '-' + option.key"
                    :value="option.value"
                    :label="option.label"
                  />
                </v-radio-group>
              </field-label>
            </template>
            <field-label class="mt-8" label="Anotações">
              <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.poem.notes" />
            </field-label> </v-expansion-panel-content
        ></v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header v-if="!attendance.data.summarizedFlow"> Pt-IGA </v-expansion-panel-header>
          <v-expansion-panel-content>
            <radio-options
              v-model="phaseData.ptiga.value"
              :options="ptigaOptions"
              :label="'Considerando as opções abaixo, como você descreveria a sua dermatite atópica?'"
              subtitle="Escolha apenas uma opção!"
            />
            <v-row>
              <v-col cols="12">
                <span class="reference"
                  >Referência: Traduzido pela Dra Ana Paula Galli, baseado no cálculo Investigator Global Assessment.
                  Bożek, Agnieszka, and Adam Reich. "Assessment of intra-and inter-rater reliability of three methods
                  for measuring atopic dermatitis severity: EASI, objective SCORAD, and IGA." Dermatology 233.1 (2017):
                  16-22.</span
                >
              </v-col>
            </v-row>
            <field-label class="mt-8" label="Anotações">
              <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.ptiga.notes" />
            </field-label> </v-expansion-panel-content
        ></v-expansion-panel>
        <v-expansion-panel>
          <v-expansion-panel-header> ADCT </v-expansion-panel-header>
          <v-expansion-panel-content>
            <field-label :label="'Pontuação total ADCT do paciente (Máximo de 24)'">
              <template #label-extra>
                <info-btn :size="'medium'" @click="setImageToShow('/assets/adct.png')" />
              </template>
              <div class="d-flex gap-2 mt-2 align-center">
                <text-field
                  :min="0"
                  type="number"
                  v-if="phaseData.adct.resultado_manual"
                  v-model="phaseData.adct.value"
                  :max="24"
                />
                <span
                  v-else
                  :class="phaseData.adct.value === '?' ? 'black--text text-h6' : 'big-number ' + adctSeverity.class"
                  >{{ !phaseData.adct.value ? '?' : phaseData.adct.value }}</span
                >
                <span v-if="phaseData.adct.value" class="ml-2" :class="adctSeverity.class" style="width: 120px">{{
                  adctSeverity.text
                }}</span>
              </div>
              <v-switch
                class="my-4"
                label="Aplicar resultado manualmente"
                v-model="phaseData.adct.resultado_manual"
                hide-details
              />
            </field-label>
            <template v-if="!phaseData.adct.resultado_manual">
              <field-label v-for="question in adctOptions.questions" :key="question.key" :label="question.name">
                <v-radio-group
                  v-model="phaseData.adct.answers[question.key].value"
                  :row="$vuetify.breakpoint.smAndDown ? false : true"
                >
                  <v-radio
                    v-for="option in question.options"
                    :key="question.key + '-' + option.key"
                    :value="option.value"
                    :label="option.label"
                  />
                </v-radio-group>
              </field-label>
            </template>
            <v-row>
              <v-col cols="12">
                <span class="reference"
                  >Referência: Atopic Dermatitis Control Tool_Versão 1, 06 Mar 2020 Sanofi Group e Regeneron
                  Pharmaceuticals Inc. Todos os direitos reservados. ADCT – Brazil/Portuguese ADCT_HCP information
                  card_Flashcard - Brazil/Portuguese - Version of 06 Mar 2020 - Mapi. ID0085-TR-0083 / ADCT_HCP
                  information card_Flashcard_AU1.0-MKTG_por-BR.doc</span
                >
              </v-col>
            </v-row>
            <field-label class="mt-8" label="Anotações">
              <text-area :readonly="disabled" :max-length="2000" v-model="phaseData.adct.notes" />
            </field-label> </v-expansion-panel-content
        ></v-expansion-panel>
      </v-expansion-panels>
    </field-label>
    <v-dialog v-model="imageDialog" max-width="800px">
      <v-img :src="imageToShow" max-width="800px" height="100%" />
    </v-dialog>
  </div>
</template>
<script>
import { DLQI_OPTIONS } from '@/components/AttendanceForm/general/constants/dlqiOptions'
import BasePhase from '@/components/AttendanceForm/general/phase/BasePhase.vue'
import FieldLabel from '@/components/FieldLabel.vue'
import GradientRange from '@/components/GradientRange.vue'
import RateTen from '@/components/RateTen.vue'
import TextArea from '@/components/TextArea.vue'
import TextField from '@/components/TextField.vue'
import { PLACEHOLDER_AVATAR_PATH } from '@/constants/global'
import AtopicDermatitisBodyModel from '../AtopicDermatitisBodyModel.vue'
import AtopicDermatitisChildBody from '../AtopicDermatitisChildBody.vue'
import { ADCT } from '../constants/adct'
import { POEM_QUESTIONS } from '../constants/poemquestions'
import { SYMPTOMS } from '../constants/symptomlevel'

import InfoBtn from '@/components/InfoBtn.vue'
import RadioOptions from '@/components/RadioOptions.vue'
import { ECustomSummmarizedflow } from '@/constants/customSummarizedFlow'
import { createNamespacedHelpers } from 'vuex'
import AreaSlider from '../../general/phase/AreaSlider.vue'

const { mapGetters: attendanceGettersMap } = createNamespacedHelpers('attendance')
const { mapGetters: authGettersMap } = createNamespacedHelpers('auth')

import scoresMixin from '@/components/AttendanceForm/atopicdermatitis/mixins/atopicdermatitisScoresMixin'

export default {
  components: {
    AtopicDermatitisBodyModel,
    AtopicDermatitisChildBody,
    FieldLabel,
    InfoBtn,
    TextArea,
    TextField,
    AreaSlider,
    RateTen,
    GradientRange,
    RadioOptions,
  },
  mixins: [scoresMixin],
  extends: BasePhase,
  data() {
    return {
      pickDlqiDate: false,
      ptigaOptions: [
        { text: 'Ausente (sem lesões)', value: 0 },
        { text: 'Pouquíssimas lesões', value: 1 },
        { text: 'Leve', value: 2 },
        { text: 'Moderada', value: 3 },
        { text: 'Grave', value: 4 },
      ],
      skinProblemTypes: [
        { key: 'eritema', name: 'Eritema' },
        { key: 'edema', name: 'Edema' },
        { key: 'escoriacoes', name: 'Escoriações' },
        { key: 'liquenificacao', name: 'Liquenificação' },
      ],
      lazySource: PLACEHOLDER_AVATAR_PATH,
      igaOptions: [
        {
          value: 0,
          intensity: 'Sem lesões',
          description: 'Pode ter hiper ou hipopigmentação pós inflamatória.',
        },
        {
          value: 1,
          intensity: 'Praticamente sem lesões',
          description: 'Eritema, pápulas e/ou liquenificação quase imperceptíveis. Sem crostas ou exsudação.',
        },
        {
          value: 2,
          intensity: 'Leve',
          description: 'Eritema, pápulas e/ou liquenificação presentes. Sem crostas ou exsudação.',
        },
        {
          value: 3,
          intensity: 'Moderado',
          description: 'Eritema, pápulas e/ou liquenificação claramente visíveis. Pode ter exsudação e crostas.',
        },
        {
          value: 4,
          intensity: 'Grave',
          description: 'Eritema, pápulas e/ou liquenificação intensos. Pode ter exsudação e crostas e doença extensa.',
        },
      ],
      indicators: [
        {
          key: 'easi',
          name: 'EASI',
          max: 72,
          getComputedValue: () => this.computedEasi,
        },
        {
          key: 'bsa',
          name: 'BSA',
          max: 100,
          getComputedValue: () => this.computedBsa,
        },
      ],
      areas: [
        {
          key: 'cabeca_pescoco',
          name: 'CABEÇA E PESCOÇO',
          factor: 0.1,
          childrenFactor: 0.2,
        },
        {
          key: 'tronco',
          name: 'TRONCO',
          factor: 0.3,
        },
        {
          key: 'membros_superiores',
          name: 'MEMBROS SUPERIORES',
          factor: 0.2,
        },
        {
          key: 'membros_inferiores',
          name: 'MEMBROS INFERIORES',
          factor: 0.4,
          childrenFactor: 0.3,
        },
      ],
      imageDialog: false,
      imageToShow: null,
    }
  },
  computed: {
    ...attendanceGettersMap(['attendancePatient', 'attendance']),
    ...authGettersMap(['pharmaceuticalDomainConfig']),
    defaultDlqiOptions() {
      return [
        { text: 'Realmente muito', value: 3 },
        { text: 'Bastante', value: 2 },
        { text: 'Um pouco', value: 1 },
        { text: 'Nada', value: 0 },
      ]
    },
    computedDlqi() {
      if (!this.phaseData || this.phaseData.dlqi.manual) {
        return this.phaseData?.dlqi?.value || 0
      }

      let result = 0
      for (let option of this.dlqiOptions) {
        let value = this.phaseData.dlqi.profile[option.key]?.value || 0
        if (!parseInt(value)) {
          continue
        }

        result += value
      }
      return result
    },
    dlqiOptions() {
      return DLQI_OPTIONS.ADULT
    },
    ECustomSummmarizedflow() {
      return ECustomSummmarizedflow
    },
    affectedAreas() {
      return Object.values(this.phaseData.scorad['areas_afetadas']).filter((a) => a.selected)
    },

    poemQuestions() {
      return POEM_QUESTIONS
    },
    adctOptions() {
      return ADCT
    },
  },
  methods: {
    postPhaseDataLoaded() {
      this.changeDlqiProfileFieldValue()

      for (const area of this.areas) {
        const objKeys = Object.keys(this.phaseData.easi_bsa[area.key])
        if (objKeys.includes('descamacao') || objKeys.includes('infiltracao')) {
          const newData = {
            eritema: this.phaseData.easi_bsa[area.key].eritema,
            liquenificacao: this.phaseData.easi_bsa[area.key].liquenificacao,
            area: this.phaseData.easi_bsa[area.key].area,
          }

          if (objKeys.includes('descamacao')) {
            newData.edema = this.phaseData.easi_bsa[area.key].descamacao
          } else {
            newData.edema = this.phaseData.easi_bsa[area.key].edema
          }
          if (objKeys.includes('infiltracao')) {
            newData.escoriacoes = this.phaseData.easi_bsa[area.key].infiltracao
          } else {
            newData.escoriacoes = this.phaseData.easi_bsa[area.key].escoriacoes
          }

          this.phaseData.easi_bsa[area.key] = newData
        }
      }
    },
    getDefaultData() {
      const data = {
        scorad: {
          areas_afetadas: {},
          niveis_sintoma: {
            liquenificacao: {
              value: null,
            },
            eritema: {
              value: null,
            },
            edema: {
              value: null,
            },
            exsudacao: {
              value: null,
            },
            escoriacoes: {
              value: null,
            },
            xerose: {
              value: null,
            },
          },
          coceira: null,
          sono: null,
          notes: null,
        },
        easi_bsa: {
          easi: null,
          bsa: null,
          resultado_manual: false,
          cabeca_pescoco: {
            eritema: null,
            edema: null,
            escoriacoes: null,
            liquenificacao: null,
            area: null,
          },
          tronco: {
            eritema: null,
            edema: null,
            escoriacoes: null,
            liquenificacao: null,
            area: null,
          },
          membros_superiores: {
            eritema: null,
            edema: null,
            escoriacoes: null,
            liquenificacao: null,
            area: null,
          },
          membros_inferiores: {
            eritema: null,
            edema: null,
            escoriacoes: null,
            liquenificacao: null,
            area: null,
          },
          notes: null,
        },
        dlqi: {
          value: null,
          manual: false,
          date: null,
          profile: this.buildEmptyProfile(),
        },
        iga: {
          value: null,
          notes: null,
        },
        winrs: {
          value: null,
          notes: null,
        },
        poem: {
          value: null,
          resultado_manual: false,
          answers: {
            coceira: { value: null },
            sono: { value: null },
            sangramento: { value: null },
            vazamento: { value: null },
            rachaduras: { value: null },
            descamacao: { value: null },
            secura: { value: null },
          },
          notes: null,
        },
        ptiga: {
          value: null,
          notes: null,
        },
        adct: {
          value: 0,
          resultado_manual: false,
          answers: {
            sintomas: { value: null },
            coceira: { value: null },
            eczema: { value: null },
            sono: { value: null },
            atividades_diarias: { value: null },
            humor: { value: null },
          },
          notes: null,
        },
      }
      return data
    },
    symptomOptions() {
      let symptoms = [...SYMPTOMS]
      let col1 = symptoms.splice(0, 3)
      let col2 = symptoms
      return { col1, col2 }
    },
    buildEmptyProfile() {
      return this.dlqiOptions.reduce((acc, option) => ({ ...acc, [option.key]: { value: null } }), {})
    },
    checkDlqi(key, value) {
      if (key === 'impedimento_trabalho_estudo_atual' && this.phaseData.dlqi.profile[key].value !== 0) {
        this.phaseData.dlqi.profile['impedimento_trabalho_estudo_passado'] = { value: null }
      }
    },
    changeDlqiProfileFieldValue() {
      if (this.phaseData.dlqi.profile['impedimento_trabalho_estudo_atual'].value === 'NAO') {
        this.phaseData.dlqi.profile['impedimento_trabalho_estudo_atual'].value = 0
      }
    },
    setImageToShow(src) {
      this.imageToShow = src
      this.imageDialog = true
    },
  },
  watch: {
    computedDlqi(newVal) {
      if (this.phaseData.dlqi.manual) {
        return
      }
      this.phaseData.dlqi.value = Number(newVal)
    },
    computedEasi(newVal) {
      if (this.phaseData.easi_bsa.resultado_manual) {
        return
      }
      this.phaseData.easi_bsa.easi = Number(newVal).toFixed(2)
    },
    computedBsa(newVal) {
      if (this.phaseData.easi_bsa.resultado_manual) {
        return
      }
      this.phaseData.easi_bsa.bsa = Number(newVal).toFixed(2)
    },
    computedPoem(newVal) {
      if (this.phaseData.poem.resultado_manual) {
        return
      }
      this.phaseData.poem.value = Number(newVal).toFixed(2)
    },
    computedAdct(newVal) {
      if (this.phaseData.adct.resultado_manual) {
        return
      }
      this.phaseData.adct.value = Number(newVal).toFixed(2)
    },
  },
}
</script>

<style scoped>
.reference {
  font-weight: 400;
  font-size: 12px;
  line-height: 16px;
}

.zoomable {
  cursor: zoom-in;
}
.br-20 {
  border-radius: 20px;
  border: 2px solid #1976d2;
}
:deep(.v-input--selection-controls__input .v-icon) {
  background: white;
  border-radius: 50%;
}
.image-radio {
  position: absolute;
  right: 0;
  bottom: 0;
}
.big-number {
  font-weight: medium;
  color: green;
  font-size: 2.5rem;
}
.area-box {
  border: 1px solid grey;
  border-radius: 10px;
}
.slider-image {
  border: 4px solid #1976d2;
}
.area-component.small :deep(.col) {
  flex-basis: auto !important;
}
.table div {
  align-items: center;
}
.table div:nth-child(1n + 2) {
  border: solid 1px rgba(0, 0, 0, 0.7);
  margin-right: -1px;
  margin-bottom: -1px;
}
.table div:first-child {
  justify-content: flex-end;
}
.table div div {
  height: 100px;
}
.rate-ten {
  width: 100%;
  display: flex;
  flex-flow: column nowrap;
}
.rate-ten > div {
  align-self: center;
  width: fit-content;
  border: 1px solid black;
  padding: 12px 24px;
}
@media (max-width: 425px) {
  .table {
    justify-content: flex-end;
    margin-bottom: 12px;
  }
}
</style>
